import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import {
  App,
  isAppTypeInList,
  isMultistoreApp,
  UnderwriterDecision,
  Vorbelegungdaten,
  Vorvertrag,
  MeasureType,
  UnderwriterMeasure,
  UnderwriterDecisionWithModalData,
  FeatureToggles,
  BasisAdresse,
} from '@ps20-frontend/frontend-models';
import {
  getAllUwContexts,
  isUwEntryActive,
  selectActiveUwEntries,
  selectEnabledUwContexts,
  selectIsUwComplete,
  selectUwEntries,
  UwContextEntry,
  UwContextKey,
  UwSharedDecision,
} from '@ps20-frontend/uw';
import { VorbelegungPayload } from '@ps20-frontend/shop-integration-redux';
import { selectIsUnderwriter } from '@ps20-frontend/selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';

export const selectWarenkorbAngebote = (state: IAppState): Array<WarenkorbAngebot> =>
  state.warenkorb;

export const maxAngeboteTarifiert = (appId: App, state: IAppState): boolean => {
  const amountAngeboteForApp = state.warenkorb.filter(
    warenkorbAngebot => warenkorbAngebot.appId === appId && angebotIsTarifiert(warenkorbAngebot),
  ).length;
  const amountMaxAngeboteForApp = getMaxAngeboteForApp(appId);
  return amountAngeboteForApp >= amountMaxAngeboteForApp;
};

export const maxAngeboteImWarenkorb = (appId: App, state: IAppState): boolean => {
  const amountAngeboteForApp = state.warenkorb.filter(
    warenkorbAngebot => warenkorbAngebot.appId === appId,
  ).length;
  const amountMaxAngeboteForApp = getMaxAngeboteForApp(appId);
  return amountAngeboteForApp >= amountMaxAngeboteForApp;
};

export const getMaxAngeboteForApp = (appId: App): number => {
  if (isMultistoreApp(appId)) {
    switch (appId) {
      case App.UNF:
        return 9;
      case App.GEGE:
        return 2;
      default:
        return 1000;
    }
  }
  return 1;
};

export const getTarifierteAngebote = (state: IAppState): Array<WarenkorbAngebot> =>
  state.warenkorb.filter(angebotIsTarifiert);

export const angebotIsTarifiert = (produkt: WarenkorbAngebot): boolean =>
  produkt.ausgewaehlteLinie != null;

export const onlyWeitereProdukteInWarenkorb = (state: IAppState): boolean =>
  selectWarenkorbAngebote(state).every(
    warenkorbAngebot => warenkorbAngebot.appId === App.JHP || warenkorbAngebot.appId === App.GEGE,
  );

export const selectRequestVorbelegung = (
  vorvertrag: Vorvertrag,
  state: IAppState,
): {
  vorbelegungPayload: VorbelegungPayload;
  vorbelegungdaten: Vorbelegungdaten;
  produkt: WarenkorbAngebot;
} => {
  const produkt = state.warenkorb.find(
    produkt =>
      produkt.vorvertragsNummern?.some(
        nummer =>
          nummer.spLaufnummer === vorvertrag.spLaufnummer &&
          nummer.vertragsnummer === vorvertrag.vertragsnummer,
      ),
  );

  return selectVorbelegungRequestForWarenkorbProdukt(vorvertrag, produkt);
};

export const selectVorbelegungRequestForWarenkorbProdukt = (
  vorvertrag: Vorvertrag,
  produkt: WarenkorbAngebot,
): {
  vorbelegungPayload: VorbelegungPayload;
  vorbelegungdaten: Vorbelegungdaten;
  produkt: WarenkorbAngebot;
} => {
  let vorbelegungdaten: Vorbelegungdaten = vorvertrag?.vorbelegungdaten?.find(
    daten => daten.forApp === produkt.appId,
  );

  if (!vorbelegungdaten &&
      produkt.appId === App.TKV_V2 &&
      vorvertrag?.produktIds?.[0] === App.TKV) {
    vorbelegungdaten = vorvertrag.vorbelegungdaten?.find(daten => daten.forApp === App.TKV);
  }

  if (!vorbelegungdaten &&
      produkt.appId === App.VWG_V2 &&
      vorvertrag?.produktIds?.[0] === App.VWG) {
    vorbelegungdaten = vorvertrag.vorbelegungdaten?.find(daten => daten.forApp === App.VWG);
  }

  if (!vorbelegungdaten &&
      produkt.appId === App.VHV_V2 &&
      vorvertrag?.produktIds?.[0] === App.VHV) {
    vorbelegungdaten = vorvertrag.vorbelegungdaten?.find(daten => daten.forApp === App.VHV);
  }

  if (
    !vorvertrag.tarifGenerationPS20 &&
    ![App.JHP, App.GEGE].includes(produkt.appId) &&
    vorbelegungdaten
  ) {
    vorbelegungdaten.urbeginn = null;
  }
  const vorbelegungPayload: VorbelegungPayload = {
    spLaufnummer: vorvertrag.spLaufnummer,
    vertragsnummer: vorvertrag.vertragsnummer,
    vorhandeneVorbelegungdaten: vorbelegungdaten,
    appId: produkt.appId,
    angebotsId: produkt.angebotsId,
  };
  return { vorbelegungPayload, vorbelegungdaten, produkt };
};

export const hasUnderwriterInfo = (produkt: WarenkorbAngebot): boolean =>
  produkt.uw?.entries?.some(entry => entry.isRequired) ?? false;

export const selectHasAnyProduktUnderwriterInfo = (state: IAppState): boolean => {
  return state.warenkorb.some(hasUnderwriterInfo);
};

export const selectHasAnyProduktNoUnderwriterInfo = (state: IAppState): boolean => {
  return state.warenkorb.some(produkt => !hasUnderwriterInfo(produkt));
};

export const hasRequiredUwDocumentation = (
  produkt: WarenkorbAngebot,
  featureToggles: FeatureToggles,
): boolean => {
  if (!hasUnderwriterInfo(produkt)) {
    return false;
  }
  return selectIsUwComplete(produkt.uw, getAllUwContexts(), {
    featureToggles: featureToggles ?? {},
    selectedZusatzoptionen: produkt.selectedZusatzoptionen ?? [],
    isNeugeschaeft: !produkt.beibehalten,
  });
};

export const selectIsUnderwriterInfoComplete = (state: IAppState): boolean => {
  const produkte = state.warenkorb.filter(hasUnderwriterInfo);
  if (produkte.length === 0) {
    return true;
  }

  const featureToggles = state.transientState?.featureToggles;
  return produkte.every(produkt => hasRequiredUwDocumentation(produkt, featureToggles));
};

export function formatRisikoAddress(address: BasisAdresse | undefined): string {
  return address ? `${address.plz} ${address.ort}, ${address.strasse} ${address.hausnummer}` : '';
}

export const selectUnderwriterDecisionForContext = (
  produkt: WarenkorbAngebot,
  contextKey: UwContextKey,
): UnderwriterDecision | undefined => {
  const entry = selectUwEntries(produkt.uw, contextKey)[0];
  if (!entry) {
    return undefined;
  }
  const shared = produkt.uw.shared;
  return {
    appId: produkt.appId,
    angebotsId: produkt.angebotsId,
    risikoAddress: shared.risikoAddress,
    produktName: shared.produktName,
    grund: entry.reason,
    vertragZeichnen: entry.willUnderwrite,
    massnahmen: shared.measures,
    dokumentation: shared.documentation,
    timestamp: shared.timestamp,
    isAcceptedByAgent: entry.isAcceptedByAgent,
  };
};

const buildUnderwriterDecisionsWithModalData = (
  state: IAppState,
  includeUndecided: boolean,
): UnderwriterDecisionWithModalData[] => {
  const warenkorbArray: WarenkorbAngebot[] = state.warenkorb || [];
  const decisions: UnderwriterDecisionWithModalData[] = [];
  const featureToggles = state.transientState?.featureToggles;
  const descriptors = getAllUwContexts();

  warenkorbArray.filter(hasUnderwriterInfo).forEach(item => {
    (item.uw?.entries ?? [])
      .filter(
        entry =>
          isUwEntryActive(entry, descriptors, featureToggles) &&
          (includeUndecided || entry.willUnderwrite !== null),
      )
      .forEach(entry => {
        const decision = selectUnderwriterDecisionForContext(item, entry.contextKey);
        if (decision) {
          decisions.push({
            ...decision,
            underwriterName: item.uw.shared.underwriterName,
            bruttopreis: item.bruttopreis,
            contextKey: entry.contextKey,
          });
        }
      });
  });

  return decisions;
};

export const selectUnderwriterDecisionsWithModalData = (
  state: IAppState,
): UnderwriterDecisionWithModalData[] => buildUnderwriterDecisionsWithModalData(state, false);

export const selectUnderwriterDecisions = (state: IAppState): UnderwriterDecision[] =>
  buildUnderwriterDecisionsWithModalData(state, false);

export const selectHasUwDecisions = (state: IAppState): boolean => {
  return selectUnderwriterDecisions(state).length > 0;
};

// Sibling of selectUnderwriterDecisionsWithModalData for the underwriter's review page: that
// selector deliberately excludes entries with willUnderwrite === null so the agent's post-decision
// massnahmen summary never shows a not-yet-decided entry. The underwriter's review page needs the
// opposite: a freshly triggered entry (willUnderwrite still null) must appear here, otherwise the
// underwriter-decision-card that lets them record a first-time decision never renders at all.
export const selectUnderwriterDecisionsForReview = (
  state: IAppState,
): UnderwriterDecisionWithModalData[] => buildUnderwriterDecisionsWithModalData(state, true);

function isMassnahmeValid(massnahme: UnderwriterMeasure): boolean {
  if (massnahme.type === MeasureType.ZUSCHLAG) {
    return (massnahme.zuschlagProzent ?? 0) >= 1;
  }
  if (massnahme.type === MeasureType.INDIVIDUELLE_VEREINBARUNG) {
    return Boolean(massnahme.vereinbarungText?.trim());
  }
  return true;
}

function hasValidUnderwriterIdentity(shared: UwSharedDecision): boolean {
  return Boolean(shared.underwriterBensl?.trim() && shared.underwriterName?.trim());
}

function isEntryDecisionComplete(
  entry: UwContextEntry | undefined,
  shared: UwSharedDecision,
  checkMassnahmen: boolean,
): boolean {
  return (
    entry?.willUnderwrite != null &&
    Boolean(shared.documentation?.trim()) &&
    (!checkMassnahmen || shared.measures.every(isMassnahmeValid))
  );
}

export function isUwProductComplete(
  produkt: WarenkorbAngebot,
  featureToggles?: FeatureToggles,
): boolean {
  const uw = produkt.uw;
  if (!uw) {
    return false;
  }
  if (!hasValidUnderwriterIdentity(uw.shared)) {
    return false;
  }

  const activeEntries = selectActiveUwEntries(uw, getAllUwContexts(), featureToggles);
  if (activeEntries.length === 0) {
    const settledEntries = selectUwEntries(uw);
    return (
      settledEntries.length > 0 &&
      settledEntries.every(entry => isEntryDecisionComplete(entry, uw.shared, false))
    );
  }

  return activeEntries.every(entry => isEntryDecisionComplete(entry, uw.shared, true));
}

export const selectAllUwDecisionsComplete = (state: IAppState): boolean => {
  const featureToggles = state.transientState?.featureToggles;
  const warenkorbArray: WarenkorbAngebot[] = state.warenkorb || [];

  const produkteRequiringDecision = warenkorbArray.filter(hasUnderwriterInfo);

  if (produkteRequiringDecision.length === 0) {
    return false;
  }

  return produkteRequiringDecision.every(produkt =>
    isUwProductComplete(produkt, featureToggles),
  );
};

export const selectHasUncheckedUwDecisions = (state: IAppState): boolean => {
  const decisions = selectUnderwriterDecisions(state);
  if (decisions.length === 0) {
    return false;
  }
  return decisions
    .filter(decision => decision.vertragZeichnen !== false)
    .some(
      decision =>
        (decision.massnahmen || []).some(
          massnahme => massnahme.type !== MeasureType.KEINE_MASSNAHME,
        ) && !decision.isAcceptedByAgent,
    );
};

const isUwDecisionSettled = (
  entry: UwContextEntry,
  shared: UwSharedDecision,
  produkt: WarenkorbAngebot,
  descriptors: ReturnType<typeof getAllUwContexts>,
): boolean => {
  if (entry.willUnderwrite === false) {
    const descriptor = descriptors.find(candidate => candidate.key === entry.contextKey);
    const isBlocking = descriptor?.isBlockingRisk?.(produkt) === true;
    return !isBlocking && Boolean(entry.isAcceptedByAgent);
  }

  return shared.measures.every(massnahme => massnahme.type === MeasureType.KEINE_MASSNAHME) ||
    Boolean(entry.isAcceptedByAgent);
};

export const selectHasNoUncheckedUwDecisions = (state: IAppState): boolean => {
  const featureToggles = state.transientState?.featureToggles;
  const descriptors = getAllUwContexts();
  const isActiveDecision = (entry: UwContextEntry): boolean =>
    isUwEntryActive(entry, descriptors, featureToggles) && entry.willUnderwrite !== null;

  const produkteWithUwDecisions = state.warenkorb.filter(produkt =>
    produkt.uw?.entries.some(isActiveDecision),
  );

  if (produkteWithUwDecisions.length === 0) {
    return true;
  }

  return produkteWithUwDecisions.every(produkt =>
    produkt.uw.entries
      .filter(isActiveDecision)
      .every(entry => isUwDecisionSettled(entry, produkt.uw.shared, produkt, descriptors)),
  );
};

export const selectUwUploadIds = (state: IAppState): string[] => {
  if (!state.warenkorb || state.warenkorb.length === 0) {
    return [];
  }

  const enabledKeys = new Set(
    selectEnabledUwContexts(getAllUwContexts(), state.transientState?.featureToggles).map(
      descriptor => descriptor.key,
    ),
  );

  return state.warenkorb
    .filter(produkt => isAppTypeInList(produkt?.appId, [App.VWG, App.VHV]))
    .flatMap((produkt: WarenkorbAngebot) =>
      selectUwEntries(produkt?.uw)
        .filter(entry => enabledKeys.has(entry.contextKey))
        .flatMap(entry => entry.files),
    )
    .map(file => file.uploadId)
    .filter(uploadId => uploadId && uploadId.trim().length > 0);
};

export const selectShouldBlockSpeichernExportForUw = (state: IAppState): boolean => {
  const isUnderwriter = selectIsUnderwriter(state);
  const isUnderReview = state.transientState.underReview === true;

  if (isUnderwriter) {
    return false;
  }

  if (!isUnderReview) {
    return false;
  }

  return state.warenkorb.some((produkt: WarenkorbAngebot) => {
    return (
      produkt.uw?.entries.some(entry => entry.isRequired && entry.files.length > 0) ?? false
    );
  });
};
